import React, { useEffect, useState } from 'react';
import { Table, Button, Space, message, Modal, Form, Input, Select, Tag, Popconfirm } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { http } from '../../utils';
import qs from 'qs';
import './index.scss';

const { Option } = Select;

const ReferralCode = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [form] = Form.useForm();
  const [tableParams, setTableParams] = useState({
    code: '',
    region: '',
    pagination: {
      current: 1,
      pagesize: 10
    }
  });

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    { title: 'Region', dataIndex: 'region', key: 'region', width: 120 },
    { title: 'Country Code', dataIndex: 'countryCode', key: 'countryCode', width: 120 },
    { title: 'Code', dataIndex: 'code', key: 'code', width: 150 },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => (
        <Tag color={status === '0' ? 'green' : 'red'}>
          {status === '0' ? 'Normal' : 'Frozen'}
        </Tag>
      )
    },
    { title: 'Marks', dataIndex: 'marks', key: 'marks', width: 150, ellipsis: true },
    { title: 'Created At', dataIndex: 'createdAt', key: 'createdAt', width: 180 },
    {
      title: 'Action',
      key: 'action',
      width: 200,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            size="small"
            loading={actionLoadingId === `status_${record.id}`}
            disabled={actionLoadingId && actionLoadingId !== `status_${record.id}`}
            onClick={() => handleToggleStatus(record)}
          >
            {record.status === '0' ? 'Freeze' : 'Unfreeze'}
          </Button>
          <Popconfirm
            title="Are you sure to delete?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="link"
              size="small"
              danger
              loading={actionLoadingId === `delete_${record.id}`}
              disabled={actionLoadingId && actionLoadingId !== `delete_${record.id}`}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  const loadData = async () => {
    setLoading(true);
    try {
      const params = {
        current: tableParams.pagination.current,
        pagesize: tableParams.pagination.pagesize,
        code: tableParams.code || undefined,
        region: tableParams.region || undefined
      };
      const { results, total } = await http.get(`/api/referral-code/list?${qs.stringify(params)}`);
      if (results) {
        setData(results);
        setTableParams(prev => ({
          ...prev,
          pagination: { ...prev.pagination, total }
        }));
      }
    } catch (e) {
      message.error('Failed to load data');
    }
    setLoading(false);
  };

  const deps = JSON.stringify({
    current: tableParams.pagination?.current,
    pagesize: tableParams.pagination?.pagesize,
    code: tableParams.code,
    region: tableParams.region
  });

  useEffect(() => {
    loadData();
  }, [deps]);

  const handleTableChange = (pagination) => {
    setTableParams(prev => ({
      ...prev,
      pagination: {
        ...prev.pagination,
        current: pagination.current,
        pagesize: pagination.pageSize
      }
    }));
  };

  const handleToggleStatus = async (record) => {
    if (actionLoadingId) return;
    setActionLoadingId(`status_${record.id}`);
    const newStatus = record.status === '0' ? '1' : '0';
    try {
      const { code } = await http.post('/api/referral-code/update-status', {
        id: record.id,
        status: newStatus
      });
      if (parseInt(code, 10) === 0) {
        message.success('Status updated');
        loadData();
      }
    } catch (e) {
      message.error('Failed to update status');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleDelete = async (id) => {
    if (actionLoadingId) return;
    setActionLoadingId(`delete_${id}`);
    try {
      const { code } = await http.post('/api/referral-code/delete', { id });
      if (parseInt(code, 10) === 0) {
        message.success('Deleted successfully');
        loadData();
      }
    } catch (e) {
      message.error('Failed to delete');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleAdd = async () => {
    if (submitLoading) return;
    try {
      const values = await form.validateFields();
      setSubmitLoading(true);
      const { code } = await http.post('/api/referral-code/add', values);
      if (parseInt(code, 10) === 0) {
        message.success('Added successfully');
        setModalOpen(false);
        form.resetFields();
        loadData();
      }
    } catch (e) {
      if (e.errorFields) return;
      message.error('Failed to add');
    } finally {
      setSubmitLoading(false);
    }
  };

  const onSearch = (value) => {
    setTableParams(prev => ({
      ...prev,
      code: value,
      pagination: { ...prev.pagination, current: 1 }
    }));
  };

  const onRegionChange = (value) => {
    setTableParams(prev => ({
      ...prev,
      region: value || '',
      pagination: { ...prev.pagination, current: 1 }
    }));
  };

  return (
    <div>
      <div className="referral-header">Referral Code</div>
      <div className="referral-content">
        <div className="referral-toolbar">
          <Space>
            Code:
            <Input.Search
              placeholder="Search code"
              onSearch={onSearch}
              style={{ width: 200 }}
              allowClear
            />
            Region:
            <Select
              style={{ width: 120 }}
              onChange={onRegionChange}
              allowClear
              placeholder="All"
            >
              <Option value="">All</Option>
              <Option value="EU">EU</Option>
              <Option value="US">US</Option>
              <Option value="UK">UK</Option>
            </Select>
          </Space>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalOpen(true)}>
            Add
          </Button>
        </div>
        <Table
          columns={columns}
          rowKey={(record) => record.id}
          dataSource={data}
          pagination={{
            current: tableParams.pagination.current,
            pageSize: tableParams.pagination.pagesize,
            total: tableParams.pagination.total
          }}
          loading={loading}
          onChange={handleTableChange}
          scroll={{ x: 1200 }}
        />
        <Modal
          title="Add Referral Code"
          open={modalOpen}
          onOk={handleAdd}
          confirmLoading={submitLoading}
          onCancel={() => { setModalOpen(false); form.resetFields(); }}
          destroyOnClose
        >
          <Form form={form} layout="vertical">
            <Form.Item
              label="Region"
              name="region"
              rules={[{ required: true, message: 'Please input region' }]}
            >
              <Input placeholder="e.g. EU" />
            </Form.Item>
            <Form.Item
              label="Country Code"
              name="countryCode"
              rules={[{ required: true, message: 'Please input country code' }]}
            >
              <Input placeholder="e.g. DE" />
            </Form.Item>
            <Form.Item
              label="Code"
              name="code"
              rules={[{ required: true, message: 'Please input code' }]}
            >
              <Input placeholder="Referral code" />
            </Form.Item>
            <Form.Item label="Marks" name="marks">
              <Input.TextArea rows={3} placeholder="Optional notes" />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </div>
  );
};

export default ReferralCode;
